import React from 'react';
import clsx from 'clsx';
import { ElementType } from 'react';

// Define the possible variants for the typography
type HeaderVariant =
    | 'hd-6xl'
    | 'hd-5xl'
    | 'hd-4xl'
    | 'hd-3xl'
    | 'hd-2xl'
    | 'hd-xl'
    | 'hd-lg'
    | 'hd-md';

type BodyVariant = 'body-lg' | 'body-md' | 'body-sm' | 'body-xs' | 'body-xxs';

type BodyVariantBold = 'button-md' | 'button-sm' | 'button-xs' | 'button-xxs';

type CaptionVariant = 'caption-md' | 'caption-sm' | 'caption-xs' | 'caption-xxs';

export type TypographyVariant = HeaderVariant | BodyVariant | BodyVariantBold | CaptionVariant;

type TypographyProps = {
    as?: ElementType;
    children: React.ReactNode;
    variant?: TypographyVariant;
    desktopVariant?: TypographyVariant; // New prop for large screen variants
    className?: string;
};

export const Typography = ({
    as: Component = 'p',
    children,
    variant = 'body-md',
    desktopVariant,
    className = '',
    ...props
}: TypographyProps) => (
    <Component
        lang="no"
        className={clsx(
            {
                // Header variants
                'text-hd-6xl font-fk-screamer uppercase': variant === 'hd-6xl',
                'text-hd-5xl font-fk-screamer uppercase': variant === 'hd-5xl',
                'text-hd-4xl font-fk-screamer uppercase': variant === 'hd-4xl',
                'text-hd-3xl font-fk-screamer uppercase': variant === 'hd-3xl',
                'text-hd-2xl font-fk-screamer uppercase': variant === 'hd-2xl',
                'text-hd-xl font-acumin-pro-condensed uppercase': variant === 'hd-xl',
                'text-hd-lg font-acumin-pro-condensed uppercase': variant === 'hd-lg',
                'text-hd-md font-acumin-pro-condensed uppercase': variant === 'hd-md',

                // Body variants - regular
                'text-body-lg font-acumin-pro-condensed': variant === 'body-lg',
                'text-body-md font-acumin-pro-condensed': variant === 'body-md',
                'text-body-sm font-acumin-pro-condensed': variant === 'body-sm',
                'text-body-xs font-acumin-pro-condensed': variant === 'body-xs',
                'text-body-xxs font-acumin-pro-condensed': variant === 'body-xxs',

                // Body variants - bold
                'text-button-md font-acumin-pro-condensed': variant === 'button-md',
                'text-button-sm font-acumin-pro-condensed': variant === 'button-sm',
                'text-button-xs font-acumin-pro-condensed': variant === 'button-xs',
                'text-button-xxs font-acumin-pro-condensed': variant === 'button-xxs',

                // Captin variants
                'text-caption-md font-acumin-pro-condensed': variant === 'caption-md',
                'text-caption-sm font-acumin-pro-condensed': variant === 'caption-sm',
                'text-caption-xs font-acumin-pro-condensed': variant === 'caption-xs',
                'text-caption-xxs font-acumin-pro-condensed': variant === 'caption-xxs',

                // Desktop variant classes
                'lg:text-hd-6xl lg:font-fk-screamer uppercase': desktopVariant === 'hd-6xl',
                'lg:text-hd-5xl lg:font-fk-screamer uppercase': desktopVariant === 'hd-5xl',
                'lg:text-hd-4xl lg:font-fk-screamer uppercase': desktopVariant === 'hd-4xl',
                'lg:text-hd-3xl lg:font-fk-screamer uppercase': desktopVariant === 'hd-3xl',
                'lg:text-hd-2xl lg:font-fk-screamer uppercase': desktopVariant === 'hd-2xl',
                'lg:text-hd-xl lg:font-acumin-pro-condensed': desktopVariant === 'hd-xl',
                'lg:text-hd-lg lg:font-acumin-pro-condensed': desktopVariant === 'hd-lg',
                'lg:text-hd-md lg:font-acumin-pro-condensed': desktopVariant === 'hd-md',

                // Body variants - regular
                'lg:text-body-lg lg:font-acumin-pro-condensed': desktopVariant === 'body-lg',
                'lg:text-body-md lg:font-acumin-pro-condensed': desktopVariant === 'body-md',
                'lg:text-body-sm lg:font-acumin-pro-condensed': desktopVariant === 'body-sm',
                'lg:text-body-xs lg:font-acumin-pro-condensed': desktopVariant === 'body-xs',
                'lg:text-body-xxs lg:font-acumin-pro-condensed': desktopVariant === 'body-xxs',

                // Body variants - bold
                'lg:text-button-md lg:font-acumin-pro-condensed': desktopVariant === 'button-md',
                'lg:text-button-sm lg:font-acumin-pro-condensed': desktopVariant === 'button-sm',
                'lg:text-button-xs lg:font-acumin-pro-condensed': desktopVariant === 'button-xs',
                'lg:text-button-xxs lg:font-acumin-pro-condensed': desktopVariant === 'button-xxs',

                // Captin variants
                'lg:text-caption-md lg:font-acumin-pro-condensed': desktopVariant === 'caption-md',
                'lg:text-caption-sm lg:font-acumin-pro-condensed': desktopVariant === 'caption-sm',
                'lg:text-caption-xs lg:font-acumin-pro-condensed': desktopVariant === 'caption-xs',
                'lg:text-caption-xxs lg:font-acumin-pro-condensed':
                    desktopVariant === 'caption-xxs',
            },
            className
        )}
        {...props}
    >
        {children}
    </Component>
);
